import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BedDouble, Coffee, Home as HomeIcon, TreePine } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/lib/content";

const MOMENTS = [
  { icon: BedDouble, label: "Slapen", text: "Rustiger nachten, ook met het raam open.", testId: "moment-sleep" },
  { icon: Coffee, label: "Buiten zitten", text: "Een gesprek in de tuin zonder voortdurende achtergrondruis.", testId: "moment-outside" },
  { icon: HomeIcon, label: "Wonen", text: "Minder geluid dat muren en ramen binnenkomt.", testId: "moment-living" },
  { icon: TreePine, label: "Natuur", text: "Stilte in het groen van het Gooi.", testId: "moment-nature" },
];

export const Emotion = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section data-testid="emotion-section" className="py-24 md:py-32">
      <div className="mx-auto max-w-[90rem] px-5 md:px-12">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div ref={ref} className="photo-frame aspect-[4/3] shadow-xl shadow-gooi-green/15">
              <motion.img
                src={IMAGES.nature}
                alt="Luchtfoto van een groene woonwijk in het Gooi, vlak bij de A1"
                loading="lazy"
                style={{ y, scale: 1.15, willChange: "transform" }}
                className="duotone-photo h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <Reveal>
              <h2 className="font-display text-3xl font-medium leading-tight tracking-tight text-gooi-green md:text-5xl">
                Een snelweg stopt niet bij de vangrail
              </h2>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-gooi-green/75 md:text-lg">
                Het geluid van de A1 is er niet alleen tijdens de spits. Het is er 's ochtends vroeg, overdag,
                's avonds en 's nachts. In tuinen, slaapkamers en woonwijken.
              </p>
              <p className="mt-4 max-w-2xl font-display text-xl font-medium leading-snug tracking-tight text-gooi-green md:text-2xl">
                Voor een automobilist gaat het om ongeveer één minuut. Voor omwonenden gaat het om iedere dag
                opnieuw.
              </p>
            </Reveal>
            <div className="mt-12 grid grid-cols-2 gap-6 md:grid-cols-4">
              {MOMENTS.map((m, i) => (
                <Reveal key={m.label} delay={i * 0.1}>
                  <div data-testid={m.testId} className="border-t border-gooi-green/15 pt-5">
                    <m.icon className="h-6 w-6 text-gooi-forest" strokeWidth={1.5} aria-hidden="true" />
                    <p className="mt-3 font-display text-lg font-medium tracking-tight text-gooi-green">{m.label}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-gooi-green/65">{m.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
